import React, { useState, useEffect, useCallback } from 'react';
import { Cog6ToothIcon, CheckCircleIcon, GlobeAltIcon, BellIcon, CurrencyDollarIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import Layout from '../../components/Layout';

const C = { teal:'#00B4D8', success:'#10B981', dark:'#1F2937', grey:'#6B7280', border:'#E5E7EB', warn:'#F59E0B', red:'#EF4444', purple:'#8B5CF6' };

const API = process.env.REACT_APP_API_URL || '';

function Toggle({ value, onChange }) {
  return (
    <div onClick={() => onChange(!value)} style={{ width:44, height:24, borderRadius:12, background: value ? C.success : '#D1D5DB', cursor:'pointer', position:'relative', transition:'background 0.2s', flexShrink:0 }}>
      <div style={{ position:'absolute', top:2, left: value ? 22 : 2, width:20, height:20, borderRadius:'50%', background:'#fff', transition:'left 0.2s', boxShadow:'0 1px 3px rgba(0,0,0,0.2)' }} />
    </div>
  );
}

function Section({ title, icon: Icon, children, accent }) {
  return (
    <div className="card" style={{ marginBottom:20 }}>
      <div style={{ padding:'18px 24px', borderBottom:`1px solid ${C.border}`, display:'flex', alignItems:'center', gap:10 }}>
        <Icon style={{ width:18, height:18, color: accent || C.teal }} />
        <h3 style={{ margin:0, fontSize:15, fontWeight:700 }}>{title}</h3>
      </div>
      <div style={{ padding:24 }}>{children}</div>
    </div>
  );
}

function Field({ label, desc, children }) {
  return (
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
      <div>
        <div style={{ fontSize:14, fontWeight:600 }}>{label}</div>
        {desc && <div style={{ fontSize:12, color:C.grey, marginTop:2 }}>{desc}</div>}
      </div>
      {children}
    </div>
  );
}

// Telegram icon (inline SVG)
function TelegramIcon({ style }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" style={style}>
      <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.447 1.394c-.16.16-.295.295-.605.295l.213-3.053 5.56-5.023c.242-.213-.054-.333-.373-.12l-6.871 4.326-2.962-.924c-.643-.204-.657-.643.136-.953l11.57-4.461c.537-.194 1.006.131.833.941z"/>
    </svg>
  );
}

export default function SuperAdminSettings() {
  const token = localStorage.getItem('token');
  const [saved, setSaved] = useState(false);

  // Platform
  const [platformName, setPlatformName] = useState('SpeakIQ');
  const [supportEmail, setSupportEmail] = useState('support@speakiq.uk');
  const [timezone, setTimezone] = useState('Europe/London');
  const [maintenanceMode, setMaintenanceMode] = useState(false);

  // Features
  const [aiTutor, setAiTutor] = useState(true);
  const [tutorApplications, setTutorApplications] = useState(true);
  const [certIssuance, setCertIssuance] = useState(true);
  const [placementTest, setPlacementTest] = useState(true);
  const [trialMode, setTrialMode] = useState(true);

  // Cost limits
  const [platformDailyLimit, setPlatformDailyLimit] = useState('20');
  const [studentDailyLimit, setStudentDailyLimit] = useState('2');
  const [studentMonthlyLimit, setStudentMonthlyLimit] = useState('5');
  const [freeQuestionsPerLesson, setFreeQuestionsPerLesson] = useState('3');

  // Notifications
  const [newPartnerAlert, setNewPartnerAlert] = useState(true);
  const [costAlerts, setCostAlerts] = useState(true);
  const [weeklyReport, setWeeklyReport] = useState(true);
  const [errorAlerts, setErrorAlerts] = useState(true);

  // Security
  const [twoFARequired, setTwoFARequired] = useState(false);
  const [sessionTimeout, setSessionTimeout] = useState('30');
  const [maxLoginAttempts, setMaxLoginAttempts] = useState('5');

  // Telegram
  const [tgConnected, setTgConnected] = useState(false);
  const [tgChatId, setTgChatId] = useState('');
  const [tgToken, setTgToken] = useState('');
  const [tgSaving, setTgSaving] = useState(false);
  const [tgTesting, setTgTesting] = useState(false);
  const [tgMsg, setTgMsg] = useState(null);

  const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };
  const inputStyle = { padding:'8px 12px', border:`1px solid ${C.border}`, borderRadius:8, fontSize:13, outline:'none', minWidth:200 };

  const loadTgSettings = useCallback(() => {
    fetch(`${API}/api/superadmin/telegram/settings`, { headers })
      .then(r => r.json())
      .then(d => {
        setTgConnected(d.connected || false);
        setTgChatId(d.chat_id || '');
      })
      .catch(() => {});
  }, [token]);

  useEffect(() => { loadTgSettings(); }, [loadTgSettings]);

  const saveTgSettings = async () => {
    setTgSaving(true); setTgMsg(null);
    try {
      const body = { chat_id: tgChatId };
      if (tgToken.trim()) body.bot_token = tgToken.trim();
      const r = await fetch(`${API}/api/superadmin/telegram/save`, { method:'POST', headers, body: JSON.stringify(body) });
      const d = await r.json();
      if (d.success) { setTgMsg({ ok: true, text: 'Settings saved!' }); loadTgSettings(); }
      else setTgMsg({ ok: false, text: d.error || 'Save failed' });
    } catch { setTgMsg({ ok: false, text: 'Network error' }); }
    setTgSaving(false);
  };

  const testTelegram = async () => {
    setTgTesting(true); setTgMsg(null);
    try {
      const r = await fetch(`${API}/api/superadmin/telegram/test`, { method:'POST', headers, body: JSON.stringify({ chat_id: tgChatId }) });
      const d = await r.json();
      if (d.success) setTgMsg({ ok: true, text: 'Test message sent! Check your Telegram.' });
      else setTgMsg({ ok: false, text: d.error || 'Test failed' });
    } catch { setTgMsg({ ok: false, text: 'Network error' }); }
    setTgTesting(false);
  };

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  return (
    <Layout role="superadmin">
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>
        <div>
          <Section title="Platform" icon={GlobeAltIcon}>
            <Field label="Platform Name" desc="Displayed across the platform">
              <input value={platformName} onChange={e => setPlatformName(e.target.value)} style={inputStyle} />
            </Field>
            <Field label="Support Email" desc="Students and tutors contact this">
              <input value={supportEmail} onChange={e => setSupportEmail(e.target.value)} style={inputStyle} />
            </Field>
            <Field label="Timezone" desc="Used for cron jobs and reports">
              <select value={timezone} onChange={e => setTimezone(e.target.value)} style={inputStyle}>
                {['Europe/London','Asia/Tashkent','Asia/Almaty','Asia/Bishkek','Europe/Moscow'].map(t => <option key={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Maintenance Mode" desc="Blocks all student and tutor logins">
              <Toggle value={maintenanceMode} onChange={setMaintenanceMode} />
            </Field>
          </Section>

          <Section title="Cost Circuit Breakers" icon={CurrencyDollarIcon}>
            <Field label="Platform Daily Limit" desc="Blocks all AI calls if exceeded ($)">
              <input value={platformDailyLimit} onChange={e => setPlatformDailyLimit(e.target.value)} style={{ ...inputStyle, minWidth:100 }} type="number" />
            </Field>
            <Field label="Student Daily Limit" desc="Per student per day ($)">
              <input value={studentDailyLimit} onChange={e => setStudentDailyLimit(e.target.value)} style={{ ...inputStyle, minWidth:100 }} type="number" />
            </Field>
            <Field label="Student Monthly Limit" desc="Per student per month ($)">
              <input value={studentMonthlyLimit} onChange={e => setStudentMonthlyLimit(e.target.value)} style={{ ...inputStyle, minWidth:100 }} type="number" />
            </Field>
            <Field label="Free Questions / Lesson" desc="Max AI Q&A questions per lesson">
              <input value={freeQuestionsPerLesson} onChange={e => setFreeQuestionsPerLesson(e.target.value)} style={{ ...inputStyle, minWidth:100 }} type="number" />
            </Field>
          </Section>

          {/* ── Telegram Notifications ── */}
          <Section title="Personal Telegram Notifications" icon={TelegramIcon} accent="#229ED9">
            {/* Status badge */}
            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:20, padding:'10px 14px', background: tgConnected ? '#F0FDF4' : '#FFF7ED', borderRadius:8, border:`1px solid ${tgConnected ? '#BBF7D0' : '#FED7AA'}` }}>
              <div style={{ width:8, height:8, borderRadius:'50%', background: tgConnected ? C.success : C.warn, flexShrink:0 }} />
              <span style={{ fontSize:13, fontWeight:600, color: tgConnected ? '#166534' : '#92400E' }}>
                {tgConnected ? 'Connected — @UHNITradeBot' : 'Not connected'}
              </span>
            </div>

            <Field label="Personal Chat ID" desc="Your Telegram user ID (messages go to your private chat)">
              <input value={tgChatId} onChange={e => setTgChatId(e.target.value)} style={{ ...inputStyle, minWidth:140 }} placeholder="e.g. 50861874" />
            </Field>
            <Field label="Bot Token" desc="Leave blank to keep existing token">
              <input value={tgToken} onChange={e => setTgToken(e.target.value)} style={{ ...inputStyle, minWidth:200 }} placeholder="Optional — already configured" type="password" />
            </Field>

            <div style={{ display:'flex', gap:10, marginBottom:16 }}>
              <button onClick={saveTgSettings} disabled={tgSaving} style={{ padding:'9px 20px', background: C.teal, color:'#fff', border:'none', borderRadius:8, fontSize:13, fontWeight:700, cursor:'pointer', opacity: tgSaving ? 0.7 : 1 }}>
                {tgSaving ? 'Saving…' : 'Save Settings'}
              </button>
              <button onClick={testTelegram} disabled={tgTesting} style={{ padding:'9px 20px', background:'#229ED9', color:'#fff', border:'none', borderRadius:8, fontSize:13, fontWeight:700, cursor:'pointer', opacity: tgTesting ? 0.7 : 1 }}>
                {tgTesting ? 'Sending…' : '📨 Send Test Message'}
              </button>
            </div>

            {tgMsg && (
              <div style={{ padding:'10px 14px', borderRadius:8, background: tgMsg.ok ? '#F0FDF4' : '#FEF2F2', border:`1px solid ${tgMsg.ok ? '#BBF7D0' : '#FECACA'}`, fontSize:13, color: tgMsg.ok ? '#166534' : '#991B1B' }}>
                {tgMsg.ok ? '✅ ' : '❌ '}{tgMsg.text}
              </div>
            )}

            <div style={{ marginTop:20, paddingTop:16, borderTop:`1px solid ${C.border}` }}>
              <div style={{ fontSize:13, fontWeight:600, marginBottom:10, color: C.dark }}>Active Alerts</div>
              {[
                { label:'New partner registered', sub:'When a school signs up' },
                { label:'Partner suspended / blocked', sub:'Account status changes' },
                { label:'New student registration', sub:'Public self-registration' },
                { label:'API cost exceeded threshold', sub:'Daily spend alert' },
                { label:'Server down / restored', sub:'Healthcheck alerts' },
                { label:'Payment overdue', sub:'Students with pending payment 7d+' },
                { label:'Daily report (8:00 AM UTC)', sub:'Every morning digest' },
                { label:'Weekly summary (Mon 9:00 AM UTC)', sub:'Revenue & platform stats' },
              ].map(({ label, sub }) => (
                <div key={label} style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
                  <div style={{ width:8, height:8, borderRadius:'50%', background: C.success, flexShrink:0 }} />
                  <div>
                    <div style={{ fontSize:13, fontWeight:600 }}>{label}</div>
                    <div style={{ fontSize:11, color:C.grey }}>{sub}</div>
                  </div>
                </div>
              ))}
            </div>
          </Section>
        </div>

        <div>
          <Section title="Features" icon={Cog6ToothIcon}>
            <Field label="AI Tutor" desc="Enables the AI-powered lesson engine"><Toggle value={aiTutor} onChange={setAiTutor} /></Field>
            <Field label="Tutor Applications" desc="Allow tutors to apply via tutors.speakiq.uk"><Toggle value={tutorApplications} onChange={setTutorApplications} /></Field>
            <Field label="Certificate Issuance" desc="Automatically issue CEFR certificates"><Toggle value={certIssuance} onChange={setCertIssuance} /></Field>
            <Field label="Placement Test" desc="Adaptive placement test for new students"><Toggle value={placementTest} onChange={setPlacementTest} /></Field>
            <Field label="Trial Accounts" desc="Allow 7-day free trials for new students"><Toggle value={trialMode} onChange={setTrialMode} /></Field>
          </Section>

          <Section title="Notifications" icon={BellIcon}>
            <Field label="New Partner Alerts" desc="Email when a new partner signs up"><Toggle value={newPartnerAlert} onChange={setNewPartnerAlert} /></Field>
            <Field label="Cost Alerts" desc="Alert when daily spend exceeds 80% limit"><Toggle value={costAlerts} onChange={setCostAlerts} /></Field>
            <Field label="Weekly Summary Report" desc="Every Monday morning digest"><Toggle value={weeklyReport} onChange={setWeeklyReport} /></Field>
            <Field label="Error Alerts" desc="Instant alert on 5xx errors"><Toggle value={errorAlerts} onChange={setErrorAlerts} /></Field>
          </Section>

          <Section title="Security" icon={ShieldCheckIcon}>
            <Field label="Require 2FA for Admins" desc="All admin accounts must use 2-factor auth"><Toggle value={twoFARequired} onChange={setTwoFARequired} /></Field>
            <Field label="Session Timeout (days)" desc="Auto-logout after inactivity">
              <input value={sessionTimeout} onChange={e => setSessionTimeout(e.target.value)} style={{ ...inputStyle, minWidth:100 }} type="number" />
            </Field>
            <Field label="Max Login Attempts" desc="Lock account after N failures">
              <input value={maxLoginAttempts} onChange={e => setMaxLoginAttempts(e.target.value)} style={{ ...inputStyle, minWidth:100 }} type="number" />
            </Field>
          </Section>
        </div>
      </div>

      {/* Save button */}
      <div style={{ display:'flex', justifyContent:'flex-end', gap:12 }}>
        <button onClick={handleSave} style={{ display:'flex', alignItems:'center', gap:8, padding:'12px 28px', background: saved ? C.success : C.teal, color:'#fff', border:'none', borderRadius:10, fontSize:14, fontWeight:700, cursor:'pointer', transition:'background 0.2s' }}>
          {saved ? <><CheckCircleIcon style={{ width:18, height:18 }} /> Saved!</> : 'Save All Settings'}
        </button>
      </div>
    </Layout>
  );
}
